import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Avatar, Box, Button, CircularProgress, Grid, Paper, Typography } from '@mui/material';
import React, { memo, useCallback, useMemo ,useEffect} from 'react';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import LockIcon from '@mui/icons-material/Lock';
import { useBreadcrumbs } from '../contexts/Breadcrumbs';
import { usePrerequisites } from '../contexts/Prerequisites';
import { useSetState, useUniqueEffect } from '../hooks';
import connectionService from '../services/Connection';
import util from '../util/index';
import DocumentTitle from '../components/common/DocumentTitle';
import { useModelForm } from '../contexts/ModelForm';
import LoadingDialog from '../components/common/LoadingDialog';
import SterisLogoPNG from '../styles/img/STERIS-Logo.png';
import ForgotPasswordForm from './forms/ForgotPasswordForm';

const AlreadyHaveTokenBtn = memo(() => {
	const { activeRecord } = useModelForm();
	const [{ strings }] = usePrerequisites();
	return (
		!activeRecord.alreadyHaveToken && (
			<Button variant="contained" type="submit" color="info" onClick={() => activeRecord.toggleTokenPresence(true)}>
				{strings.AlreadyHaveToken}
			</Button>
		)
	);
});
const SubmitButton = memo(() => {
	const { activeRecord } = useModelForm();
	const [{ strings }] = usePrerequisites();
	return (
		<Button
			disabled={activeRecord?.loading}
			startIcon={activeRecord?.loading && <CircularProgress size={14} />}
			variant="contained"
			color="info"
			type="submit"
		>
			{!activeRecord.alreadyHaveToken ? strings.Submit : strings.ChangePassword}
		</Button>
	);
});
const BackBtn = memo(() => {
	const navigate = useNavigate();
	const { activeRecord, formik } = useModelForm();
	const [{ strings }] = usePrerequisites();
	const handleClick = useCallback(() => {
		// formik.setFieldValue('clientId', undefined);
		formik.setFieldValue('token', undefined);
		formik.setFieldValue('password', undefined);
		formik.setFieldValue('confirm', undefined);

		formik.setFieldTouched('clientId', false);
		formik.setFieldTouched('token', false);
		formik.setFieldTouched('password', false);
		formik.setFieldTouched('confirm', false);
		if (activeRecord.alreadyHaveToken) {
			activeRecord.toggleTokenPresence(false);
			return;
		}
		navigate('/login');
	}, [activeRecord.alreadyHaveToken, formik.values]);
	return (
		<Button variant="contained" color="info" onClick={handleClick}>
			{!activeRecord.alreadyHaveToken ? strings.BackToLogin : strings.Back}
		</Button>
	);
});



const formActions = [{ Component: SubmitButton }, { Component: AlreadyHaveTokenBtn }, { Component: BackBtn }]
const containerProps = {
	elevation: 0,
	sx: {
		width: '110%',
		'& .MuiCardActions-root': {
			flexDirection: 'column',
			alignItems: 'center',
			'& .MuiButton-root': {
				mb: 1,
				ml: 0,
				width: '100%',
			},
		},
	},
}
const ForgotPassword = (props) => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const [{ strings }] = usePrerequisites();
	
	const [initialValues, alreadyHaveToken] = useMemo(() => {
		const result = {};
		let alreadyHaveToken = false;
		for (const [key, value] of searchParams) {
			switch (key) {
				case 'token':
					result.alreadyHaveToken = Boolean(value);
					break;
				case 'clientId':
					result.clientId = Number(value);
					break;
				default:
					result[key] = decodeURIComponent(value);
					break;
			}
			result[key] = decodeURIComponent(value);
			if (key === 'token') {
				alreadyHaveToken = Boolean(value);
			}
		}
		alreadyHaveToken = alreadyHaveToken && result.email && /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(result.email);
		return [result, alreadyHaveToken];
	}, [searchParams]);

	const [state, setState] = useSetState({
		loading: false,
		userClients: [],
		alreadyHaveToken: alreadyHaveToken,
	});

	const docTitle = strings?.ForgotPassword || 'Forgot Password';

	const toggleTokenPresence = useCallback(
		(v) => {
			setState({
				alreadyHaveToken: typeof v === 'boolean' ? v : !state.alreadyHaveToken
			});
		},
		[state.alreadyHaveToken]
	);

	const onFormSubmit = useCallback((values, { resetForm, setFieldTouched, setFieldValue, setValues }) => {
		setState({ loading: true });
		const formData = new FormData();
		formData.append('email', values.email);
		if (values.clientId) {
			formData.append('clientId', values.clientId);			
		}
		// formData.append('clientId', values.clientId);
		if (values.token && values.password) {
			formData.append('tokenKey', values.token);
			formData.append('newPassword', values.password);
		}
		fetch(connectionService.ForgotPassword, {
			method: 'POST',
			body: formData,
		})
			.then((response) => response.json())
			.then((json) => {
				const shouldChooseClient = Boolean(json.shouldChooseClient);
				setState({
					shouldChooseClient,
					loading: false,
					userClients: Array.isArray(json.userClients) ? json.userClients : [],
				});
				if (!json.success) {
					Swal.fire({
						titleText: strings.Error,
						text: strings[json.info] || json.info,
						icon: 'error',
						confirmButtonText: strings.Ok,
					});
					
					return json
				}
				if (json.info === 'Password changed successfully') {
					Swal.fire({
						titleText: strings.PasswordChanged,
						icon: 'success',
						confirmButtonText: 'OK',
					});
					setState({ loading: false });
					navigate('/login');
					return json;
				}
				if (json.info === 'OTPSent') {
					Swal.fire({
						titleText: strings.OTPSent,
						icon: 'success',
						confirmButtonText: 'OK',
						
					});
					setState({ loading: false, alreadyHaveToken: true });
					return json;
				}
				
				setValues({
					password: '',
					confirm: '',
					email: values.email,
					clientId: values.clientId || json.lastLoginClientId,
				});
				setFieldTouched('password', false);
				setState({
					shouldChooseClient,
					loading: false,
					alreadyHaveToken: !json.shouldChooseClient,
				});				
			})
			.catch((error) => {
				console.error(error);
				Swal.fire({
					text: strings['SomethingWentWrong'],
					icon: "error",
					confirmButtonText: strings.Ok,
					allowOutsideClick: false
				});
			})
			.finally(() => {
				setState({ loading: false });
			});
	}, [strings]);
	

	useUniqueEffect(() => {		
		if (searchParams.has('token')) {
			const token = searchParams.get('token');
			setState(prevState => ({
				alreadyHaveToken: !!token,
			}));
		}
	}, [searchParams]);

	

	if (!state.hospitalLogo) {
		let parseData = util.localData.loginData;
		const loginData = parseData ? parseData.tags : { HospitalName: 'ConnectAssure Technology', HospitalLogo: SterisLogoPNG, DefaultLanguage: 'en' };
		state.hospitalLogo = loginData.HospitalLogo;
		state.hospitalName = loginData.HospitalName;
		state.clientId = loginData.ClientId;
		let lang = util.localData.PTXPreferredLang || loginData.DefaultLanguage;
		state.language = lang;
		let theme = util.localData.PTXPreferredTheme || 'blue';
		state.theme = theme;
		strings.setLanguage(lang);
	}

	return (
		<Grid container sx={{ display: 'flex', alignContent: 'center', marginTop: state.showAllField ? '2%' : '6%', justifyContent: 'center', width: '100%' }}>
			{state.loading && <LoadingDialog isOpen={state.loading} message={state.loadingMessage} />}
			<Grid size="auto" style={{ display: 'flex' }}>
				<Paper sx={{ justifyContent: 'center', width: "500px", display: 'flex', gap: 1, flexDirection: 'column', padding: '40px', alignItems: 'center' }}>
					<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
						<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
							<img alt="STERIS-Logo" className="loginHospitalLogo" src={state.UpdateLogo ? state.hospitalLogo : SterisLogoPNG} style={{ maxWidth: "300px", maxHeight: "47px" }} />
							<Typography textAlign={'center'} variant='h3' mt={1} mb={1}>
								<LockIcon color="disabled" fontSize="large"/>
							</Typography>
							<Typography variant='h6'>{strings.ForgotPassword}</Typography>
							<Typography textAlign={'center'}>{strings.RegisterEmailAlter}</Typography>
						</Box>
					</Box>
					<ForgotPasswordForm
						onSubmit={onFormSubmit}
						activeRecord={{
							combos: { userClients: state.userClients },
							shouldChooseClient: state.shouldChooseClient,
							record: {},
							toggleTokenPresence,
							alreadyHaveToken: state.alreadyHaveToken,
							loading: state.loading,
							hideUnnecessaryInputs: searchParams.has('token') && state.alreadyHaveToken,
						}}
						initialValues={initialValues}
						containerProps={containerProps}
						hideCancel
						hideSubmit
						formActionsPosition="none"
						formActions={formActions}
					/>
				</Paper>
			</Grid>
		</Grid>
	);
};


export default memo(ForgotPassword);
